import React, { useEffect, useState } from "react";
import { Toaster, toast } from "sonner";
import { Shield, KeyRound, FileText, Plus, Trash2, AlertTriangle, Copy, Check } from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import api from "../lib/api";

const COMMODITIES = ["jewelry", "watches", "precious_metals", "loose_stones", "art", "collectibles", "electronics", "other"];

export default function AdminInsurance() {
  const [tab, setTab] = useState("policy");
  const [loading, setLoading] = useState(true);
  const [policy, setPolicy] = useState(null);
  const [codes, setCodes] = useState([]);
  const [reports, setReports] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, c, r] = await Promise.all([
        api.get("/admin/insurance/policy"),
        api.get("/admin/insurance/override-codes"),
        api.get("/admin/insurance/reports"),
      ]);
      setPolicy(p.data); setCodes(c.data || []); setReports(r.data || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading || !policy) return <DashboardLayout><div className="text-sm text-zinc-500">Loading…</div></DashboardLayout>;

  return (
    <DashboardLayout>
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Admin · Unisure</div>
        <h1 data-testid="insurance-title" className="font-display text-3xl font-black tracking-tighter">Insurance & Risk Policy</h1>
        <p className="text-sm text-zinc-600 mt-1 max-w-2xl">
          Configure declared-value limits, restricted ZIPs, Friday-rule enforcement, banned label terms,
          and reportable commodities. Issue override codes to bypass limits when justified.
        </p>
      </div>

      <div className="flex gap-1 border-b border-zinc-200 mb-6 overflow-x-auto">
        {[
          { k: "policy", l: "Policy", icon: Shield },
          { k: "codes", l: "Override codes", icon: KeyRound },
          { k: "reports", l: "Submitted reports", icon: FileText },
        ].map(({ k, l, icon: Icon }) => (
          <button
            key={k}
            data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
              tab === k ? "border-[var(--brand-accent)] text-zinc-900 font-medium" : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            <Icon size={14} /> {l}
          </button>
        ))}
      </div>

      {tab === "policy" && <PolicyTab policy={policy} onSaved={load} />}
      {tab === "codes" && <CodesTab codes={codes} onChange={load} />}
      {tab === "reports" && <ReportsTab reports={reports} />}
    </DashboardLayout>
  );
}

function PolicyTab({ policy, onSaved }) {
  const [draft, setDraft] = useState(policy);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setDraft(policy); }, [policy]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const patch = {
        coverage_limit_default: parseFloat(draft.coverage_limit_default),
        high_value_threshold: parseFloat(draft.high_value_threshold),
        restricted_zip_limit: parseFloat(draft.restricted_zip_limit),
        restricted_zips: (draft.restricted_zips || []).filter(Boolean),
        friday_rule_enabled: !!draft.friday_rule_enabled,
        rotate_source_address: !!draft.rotate_source_address,
        source_address_alias: draft.source_address_alias,
        banned_label_terms: (draft.banned_label_terms || []).filter(Boolean),
        reportable_commodities: (draft.reportable_commodities || []).filter(Boolean),
        label_safe_term: draft.label_safe_term,
      };
      await api.put("/admin/insurance/policy", patch);
      toast.success("Policy saved");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <div className="bg-white border border-zinc-200 rounded-md p-6 grid sm:grid-cols-2 gap-4">
        <Money label="Coverage ceiling (absolute)" value={draft.coverage_limit_default} onChange={(v) => set("coverage_limit_default", v)} testId="policy-ceiling" />
        <Money label="High-value threshold (Friday rule)" value={draft.high_value_threshold} onChange={(v) => set("high_value_threshold", v)} testId="policy-threshold" />
        <Money label="Restricted-ZIP limit (without HFP)" value={draft.restricted_zip_limit} onChange={(v) => set("restricted_zip_limit", v)} testId="policy-zip-limit" />
        <Toggle label="Enforce Friday rule (Sat / Sat-for-Monday)" checked={!!draft.friday_rule_enabled} onChange={(v) => set("friday_rule_enabled", v)} testId="policy-friday" />
        <Toggle label="Rotate source address to Logistics Dept alias" checked={!!draft.rotate_source_address} onChange={(v) => set("rotate_source_address", v)} testId="policy-rotate" />
        <Text label="Source address alias" value={draft.source_address_alias} onChange={(v) => set("source_address_alias", v)} testId="policy-alias" />
        <Text label="Sanitized commodity term on label" value={draft.label_safe_term} onChange={(v) => set("label_safe_term", v)} testId="policy-safe-term" />
      </div>

      <ChipList label="Restricted ZIPs" testId="policy-zips" values={draft.restricted_zips || []} onChange={(v) => set("restricted_zips", v)} placeholder="ZIP code" />
      <ChipList label="Banned label terms (stripped from labels)" testId="policy-banned" values={draft.banned_label_terms || []} onChange={(v) => set("banned_label_terms", v)} placeholder="e.g. gold, watches, jewelry" />

      <div>
        <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Reportable commodities (Unisure files for these only)</div>
        <div className="flex flex-wrap gap-2">
          {COMMODITIES.map((c) => {
            const on = (draft.reportable_commodities || []).includes(c);
            return (
              <button key={c} data-testid={`reportable-${c}`} onClick={() => set("reportable_commodities", on
                ? (draft.reportable_commodities || []).filter((x) => x !== c)
                : [...(draft.reportable_commodities || []), c])}
                className={`text-xs px-3 py-1.5 rounded-sm border ${on ? "bg-[var(--brand-accent)] text-white border-transparent" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"}`}>
                {c.replace(/_/g, " ")}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex justify-end">
        <button data-testid="policy-save" onClick={save} disabled={saving} className="brand-bg text-white rounded-sm px-5 py-2 text-sm disabled:opacity-50">
          {saving ? "Saving…" : "Save policy"}
        </button>
      </div>
    </div>
  );
}

function CodesTab({ codes, onChange }) {
  const [maxValue, setMaxValue] = useState("");
  const [forZip, setForZip] = useState("");
  const [hours, setHours] = useState(48);
  const [note, setNote] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [copied, setCopied] = useState(null);

  const issue = async () => {
    setIssuing(true);
    try {
      const { data } = await api.post("/admin/insurance/override-codes", {
        max_value: maxValue ? parseFloat(maxValue) : null,
        for_zip: forZip || null,
        valid_for_hours: parseInt(hours, 10) || 48,
        note,
      });
      toast.success(`Code issued: ${data.code}`);
      setMaxValue(""); setForZip(""); setNote("");
      onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setIssuing(false); }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 1500);
  };

  return (
    <div className="space-y-6">
      <div className="bg-zinc-50 border border-zinc-200 rounded-md p-5">
        <div className="font-display font-bold mb-3">Issue new override code</div>
        <div className="grid sm:grid-cols-4 gap-3">
          <Money label="Max value (optional)" value={maxValue} onChange={setMaxValue} testId="issue-max" />
          <Text label="ZIP restriction (optional)" value={forZip} onChange={setForZip} testId="issue-zip" />
          <Text label="Valid for (hours)" value={String(hours)} onChange={(v) => setHours(v)} testId="issue-hours" />
          <Text label="Internal note" value={note} onChange={setNote} testId="issue-note" />
        </div>
        <div className="mt-4 flex justify-end">
          <button data-testid="issue-btn" onClick={issue} disabled={issuing} className="brand-bg text-white rounded-sm px-4 py-2 text-sm inline-flex items-center gap-1 disabled:opacity-50">
            <Plus size={14} /> Issue code
          </button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200 text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left py-2 px-4">Code</th>
              <th className="text-left py-2 px-4">Max value</th>
              <th className="text-left py-2 px-4">ZIP</th>
              <th className="text-left py-2 px-4">Status</th>
              <th className="text-left py-2 px-4">Expires</th>
              <th className="text-left py-2 px-4">Note</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-400 py-6">No codes issued yet.</td></tr>}
            {codes.map((c) => (
              <tr key={c.code_id} className="border-b border-zinc-100">
                <td className="py-2 px-4 font-mono-plex">
                  <button data-testid={`copy-${c.code}`} onClick={() => copy(c.code)} className="inline-flex items-center gap-1 hover:text-zinc-900">
                    {c.code} {copied === c.code ? <Check size={12} className="text-emerald-600" /> : <Copy size={12} className="text-zinc-400" />}
                  </button>
                </td>
                <td className="py-2 px-4">{c.max_value ? `$${Number(c.max_value).toLocaleString()}` : "—"}</td>
                <td className="py-2 px-4">{c.for_zip || "—"}</td>
                <td className="py-2 px-4">
                  {c.used_at ? <span className="text-zinc-400">Used {c.used_at.slice(0, 10)}</span> : <span className="text-emerald-600">Active</span>}
                </td>
                <td className="py-2 px-4 text-xs text-zinc-500">{c.expires_at ? c.expires_at.slice(0, 16).replace("T", " ") : "—"}</td>
                <td className="py-2 px-4 text-xs text-zinc-600">{c.note || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ReportsTab({ reports }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-200 text-[11px] uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left py-2 px-4">Submitted</th>
            <th className="text-left py-2 px-4">Shipment</th>
            <th className="text-left py-2 px-4">Commodity</th>
            <th className="text-left py-2 px-4">Declared value</th>
            <th className="text-left py-2 px-4">Destination</th>
            <th className="text-left py-2 px-4">Status</th>
          </tr>
        </thead>
        <tbody>
          {reports.length === 0 && <tr><td colSpan={6} className="text-center text-zinc-400 py-6">No insurance reports filed yet.</td></tr>}
          {reports.map((r) => (
            <tr key={r.report_id} className="border-b border-zinc-100">
              <td className="py-2 px-4 text-xs text-zinc-600">{(r.submitted_at || "").slice(0, 16).replace("T", " ")}</td>
              <td className="py-2 px-4 font-mono-plex">{r.shipment_book_number}</td>
              <td className="py-2 px-4">{(r.commodity_category || "").replace(/_/g, " ")}</td>
              <td className="py-2 px-4">${Number(r.declared_value || 0).toLocaleString()}</td>
              <td className="py-2 px-4">{[(r.destination || {}).city, (r.destination || {}).state, (r.destination || {}).zip].filter(Boolean).join(" ")}</td>
              <td className="py-2 px-4"><StatusBadge status={r.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = status === "submitted" ? "bg-emerald-50 text-emerald-700 border-emerald-300"
    : status === "blocked" ? "bg-red-50 text-red-700 border-red-300"
    : status === "error" ? "bg-amber-50 text-amber-700 border-amber-300"
    : "bg-zinc-100 text-zinc-600 border-zinc-300";
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border rounded-sm ${cls}`}>{status}</span>;
}

function Money({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</label>
      <div className="relative mt-1">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">$</span>
        <input data-testid={testId} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-300 rounded-sm pl-7 pr-3 py-2 text-sm font-mono-plex" />
      </div>
    </div>
  );
}

function Text({ label, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</label>
      <input data-testid={testId} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full mt-1 border border-zinc-300 rounded-sm px-3 py-2 text-sm" />
    </div>
  );
}

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="inline-flex items-center gap-2 text-sm self-end">
      <input data-testid={testId} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4" />
      <span>{label}</span>
    </label>
  );
}

function ChipList({ label, values, onChange, placeholder, testId }) {
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (!values.includes(v)) onChange([...values, v]);
    setDraft("");
  };
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">{label}</div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v) => (
          <span key={v} data-testid={`${testId}-chip-${v}`} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 text-xs px-2 py-1 rounded-sm">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="text-zinc-400 hover:text-red-600"><Trash2 size={10} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid={`${testId}-input`} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder={placeholder} className="flex-1 border border-zinc-300 rounded-sm px-3 py-1.5 text-sm" />
        <button data-testid={`${testId}-add`} onClick={add} className="bg-zinc-900 text-white rounded-sm px-3 py-1.5 text-sm inline-flex items-center gap-1">
          <Plus size={12} /> Add
        </button>
      </div>
    </div>
  );
}
